import { App } from 'antd';

import { useIntl, useModel, useRequest } from '@umijs/max';

import { formatDateTime } from '@/utils';

import { findCharts, findDefectSets, findImageAngles, findSamples, findTimes } from './index';
import {
  FindChartsRequest,
  FindDefectSetResponse,
  FindImageAnglesRequest,
  FindSamplesRequest,
  FindSamplesResponse,
  FindTimesRequest,
} from './types';

export const useFindDefectSets = (deviceId: string) => useRequest(findDefectSets, {
  cacheKey: 'findDefectSets',
  formatResult: (result: FindDefectSetResponse[]) => result.map(({ id, name }) => ({ label: name, value: id })),
  manual: true,
  ready: !!deviceId,
});

export const useFindSamples = (payload: null | Partial<FindSamplesRequest>) => {
  const intl = useIntl();
  const { message } = App.useApp();
  const { setSamples } = useModel('samples');
  const { searchVersion } = useModel('showSamplesDetails');

  return useRequest(() => findSamples(payload), {
    cacheKey: 'findSamples',
    onError: () => {
      message.error(intl.formatMessage({ id: 'common.fetch.failure' }));
    },
    onSuccess: (data: FindSamplesResponse[]) => {
      setSamples(data.map(({ timestamp, ...rest }) => ({ timestamp: formatDateTime(intl, timestamp), ...rest })));
    },
    ready: !!payload,
    refreshDeps: [JSON.stringify(payload), searchVersion],
  });
};

export const useFindCharts = (payload: null | Partial<FindChartsRequest>) => {
  const intl = useIntl();
  const { message } = App.useApp();
  const { searchVersion } = useModel('showSamplesDetails');

  return useRequest(() => findCharts(payload), {
    cacheKey: 'findCharts',
    onError: () => {
      message.error(intl.formatMessage({ id: 'common.fetch.failure' }));
    },
    ready: !!payload?.feature,
    refreshDeps: [JSON.stringify(payload), searchVersion],
  });
};

export const useFindTimes = (payload: null | Partial<FindTimesRequest>) => {
  const intl = useIntl();
  const { message } = App.useApp();
  const { searchVersion } = useModel('showSamplesDetails');

  return useRequest(() => findTimes(payload), {
    cacheKey: 'findTimes',
    onError: () => {
      message.error(intl.formatMessage({ id: 'common.fetch.failure' }));
    },
    ready: !!payload,
    refreshDeps: [JSON.stringify(payload), searchVersion],
  });
};

export const useFindImageAngles = (payload: null | Partial<FindImageAnglesRequest>) => {
  const intl = useIntl();
  const { message } = App.useApp();
  const { searchVersion } = useModel('showSamplesDetails');

  return useRequest(() => findImageAngles(payload), {
    cacheKey: 'findImageAngles',
    onError: () => {
      message.error(intl.formatMessage({ id: 'common.fetch.failure' }));
    },
    ready: !!payload,
    refreshDeps: [JSON.stringify(payload), searchVersion],
  });
};
